import React from "react";
import { Link } from "react-router-dom";
import {
  Target,
  TrendingUp,
  ClipboardCheck,
  MessageSquare,
  BarChart3,
  Award,
  ArrowRight,
  Zap,
  CheckCircle2
} from "lucide-react";

export default function PerformanceAndOKRs() {
  const poppinsStyle = { fontFamily: "'Poppins', sans-serif" };
  const robotoStyle = { fontFamily: "'Roboto', sans-serif" };

  return (
    <div className="min-h-screen bg-white" style={robotoStyle}>
      {/* Hero Section - Clean & Minimal */}
      <div className="bg-white py-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-50 text-rose-600 text-xs font-medium mb-6" style={poppinsStyle}>
            <Zap size={14} fill="currentColor" />
            Performance Management
          </div>
          
          <h1 className="text-3xl md:text-4xl font-medium text-gray-900 mb-6 leading-tight" style={poppinsStyle}>
            Align Your Teams with <br />
            Measurable OKRs & Growth Tracking
          </h1>
          
          <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Bridge the gap between strategy and execution with real-time goal tracking, 
            continuous feedback, and data-driven appraisals.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-24">
        {/* Feature Grid - Tighter Layout */}
        <div className="grid md:grid-cols-3 gap-y-12 gap-x-10 mb-20">
          <FeatureItem 
            icon={<Target size={20} />} 
            title="Strategic OKRs"
            desc="Cascade company goals down to individual key results with automated tracking."
            poppins={poppinsStyle}
          />
          <FeatureItem 
            icon={<MessageSquare size={20} />} 
            title="360° Feedback"
            desc="Foster transparency with peer-to-peer and manager feedback cycles."
            poppins={poppinsStyle}
          />
          <FeatureItem 
            icon={<ClipboardCheck size={20} />} 
            title="Agile Appraisals"
            desc="Replace rigid reviews with flexible monthly or quarterly check-ins."
            poppins={poppinsStyle}
          />
          <FeatureItem 
            icon={<TrendingUp size={20} />} 
            title="Skill Mapping"
            desc="Track core competencies for every role to identify talent gaps."
            poppins={poppinsStyle}
          />
          <FeatureItem 
            icon={<Award size={20} />} 
            title="Recognition"
            desc="Celebrate wins with public shout-outs and performance badges."
            poppins={poppinsStyle}
          />
          <FeatureItem 
            icon={<BarChart3 size={20} />} 
            title="Analytics"
            desc="Identify high performers and improvement areas with visual data."
            poppins={poppinsStyle}
          />
        </div>

        {/* Benefits & Minimal Testimonial Section */}
        <div className="grid md:grid-cols-2 gap-12 items-start py-12 border-t border-slate-100">
           <div className="space-y-6">
              <h2 className="text-xl font-medium text-gray-900" style={poppinsStyle}>Why OKRs Matter</h2>
              <div className="grid gap-4">
                 <CheckLine label="Alignment: Every employee knows their contribution." />
                 <CheckLine label="Focus: Prioritize high-impact work over busy work." />
                 <CheckLine label="Transparency: Metrics eliminate ambiguity." />
              </div>
           </div>

           {/* Small, Neat Testimonial Note */}
           <div className="bg-slate-50 p-6 rounded-xl border border-slate-100">
              <p className="text-sm text-gray-600 italic leading-relaxed mb-4">
                "Shifting to structured OKRs increased our team productivity by 40% in the first two quarters. The clarity it provides is unmatched."
              </p>
              <div className="flex items-center gap-2">
                 <div className="w-8 h-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center text-[10px] font-bold">HR</div>
                 <div>
                    <p className="text-xs font-bold text-gray-900" style={poppinsStyle}>VP of People</p>
                    <p className="text-[10px] text-gray-500 uppercase tracking-tighter">Global Tech Solutions</p>
                 </div>
              </div>
           </div>
        </div>

        {/* Professional Compact CTA */}
        <div className="text-center pt-16 border-t border-slate-100">
          <h3 className="text-xl font-medium text-gray-900 mb-6" style={poppinsStyle}>Ready to build a results-driven team?</h3>
          
          <div className="flex justify-center">
            <Link 
              to="/login"
              className="group flex items-center gap-3 bg-gray-900 hover:bg-black text-white px-6 py-2.5 rounded-full transition-all duration-300 shadow-md"
            >
              <div className="text-left">
                <p className="text-[9px] uppercase tracking-widest opacity-70 leading-none mb-1" style={poppinsStyle}>Access Suite</p>
                <p className="text-sm font-semibold leading-none" style={poppinsStyle}>Get Started Now</p>
              </div>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Helper Components */
function FeatureItem({ icon, title, desc, poppins }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="w-10 h-10 rounded-lg bg-slate-50 text-rose-500 border border-slate-100 flex items-center justify-center shadow-sm">
        {icon}
      </div>
      <div>
        <h4 className="font-medium text-gray-900 text-base mb-1" style={poppins}>{title}</h4>
        <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function CheckLine({ label }) {
  return (
    <div className="flex items-center gap-3">
      <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0" />
      <p className="text-sm text-gray-600">{label}</p>
    </div>
  );
}